import { Navigate, Route, useParams, useSearchParams } from "@solidjs/router"
import { createMemo, lazy, Show, Suspense, type ParentProps } from "solid-js"
import { Home } from "@/home/route"
import { ServerProvider } from "@/runtime/server/current"
import { useGlobal } from "@/runtime/server/runtime"
import { ServerConnection, useServers } from "@/runtime/server/registry"
import { BrowserAttachmentsProvider } from "@/session/browser/attachments"
import { SessionLoadingSkeleton, SessionPanelFrame, SessionRouteFrame } from "@/session/session-frame"
import { LayoutProvider } from "@/shell/state/layout"
import { SettingsSurfaceProvider } from "@/settings/surface"
import Shell from "@/shell/shell"
import { requireServerKey } from "./session"
import { SairGate } from "@/sair/gate"
import { SairEntryConnection } from "@/sair/entry"
import { ProjectRenameSync } from "@/workspaces/rename"
import { NewSessionLoading } from "@/new-session/loading"

export const File = lazy(() => import("@opencode/session-ui/file").then((module) => ({ default: module.File })))
const loadSessionRoute = () => Promise.all([import("@/session/route"), File.preload()]).then(([module]) => module)
const DraftRoute = lazy(() => import("@/new-session/route").then((module) => ({ default: module.DraftRoute })))
const SettingsScreen = lazy(() => import("@/settings/shell").then((module) => ({ default: module.SettingsScreen })))
const ConnectServerScreen = lazy(() =>
  import("@/servers/connect/screen").then((module) => ({ default: module.ConnectServerScreen })),
)

export function preloadRoute(url: string) {
  const pathname = url.split(/[?#]/, 1)[0]
  if (pathname === "/new-session") return DraftRoute.preload().then(() => undefined)
  if (pathname === "/settings") return SettingsScreen.preload().then(() => undefined)
  if (/^\/server\/[^/]+\/session\/[^/]+$/.test(pathname)) return loadSessionRoute().then(() => undefined)
  return Promise.resolve()
}

export function AppRoutes() {
  return (
    <Route component={AppLayout}>
      <Route path="/" component={RootRoute} />
      <Route path="/settings" component={SettingsScreen} />
      <Route
        path="/server/:serverKey/session/:id"
        component={() => (
          <SessionRouteFrame>
            <TargetServerRoute />
          </SessionRouteFrame>
        )}
      />
      <Route
        path="/new-session"
        component={() => (
          <Suspense fallback={<NewSessionLoading />}>
            <DraftRoute />
          </Suspense>
        )}
      />
    </Route>
  )
}

function RootRoute() {
  const [search] = useSearchParams<{ view?: string }>()
  return (
    <Show when={search.view === "library"} fallback={<Navigate href="/new-session" />}>
      <Home />
    </Show>
  )
}

function TargetServerRoute() {
  const params = useParams<{ serverKey: string }>()
  const global = useGlobal()
  const connection = createMemo(() =>
    global.servers.list().find((item) => ServerConnection.key(item) === requireServerKey(params.serverKey)),
  )

  return (
    <Show when={connection()} keyed>
      {(connection) => {
        // Solid's lazy wrapper owns its pending resource. A new server must not
        // reuse a resource whose first route owner is being disposed mid-load.
        const TargetSessionRouteContent = lazy(() =>
          loadSessionRoute().then((module) => ({ default: module.TargetSessionRouteContent })),
        )
        return (
          <ServerProvider conn={connection}>
            <Suspense
              fallback={
                <div class="flex min-h-0 flex-1 pb-[var(--shell-bottom-inset,8px)] pt-[var(--shell-top-inset,8px)]">
                  <SessionPanelFrame raised>
                    <SessionLoadingSkeleton />
                  </SessionPanelFrame>
                </div>
              }
            >
              <TargetSessionRouteContent />
            </Suspense>
          </ServerProvider>
        )
      }}
    </Show>
  )
}

function AppLayout(props: ParentProps) {
  const servers = useServers()
  return (
    <Show
      when={servers.list.length > 0}
      fallback={
        <SairEntryConnection>
          <ConnectServerScreen />
        </SairEntryConnection>
      }
    >
      <LayoutProvider>
        <SettingsSurfaceProvider>
          <ProjectRenameSync />
          <BrowserAttachmentsProvider>
            <SairGate>
              <Shell>{props.children}</Shell>
            </SairGate>
          </BrowserAttachmentsProvider>
        </SettingsSurfaceProvider>
      </LayoutProvider>
    </Show>
  )
}
